import type { MutableRefObject } from "react";
import { useCallback, useRef, useState } from "react";

import type { Uuid } from "@/domains/global/identifiers";
import { prepareAudioRecordingDownloadUrl } from "@/modules/audio-recording/prepare-download";

export type CapturedAudioPlayback = {
  audioElementRef: MutableRefObject<HTMLAudioElement | null>;
  playbackUrl: string | null;
  play: () => Promise<void>;
  clear: () => void;
  isPreparing: boolean;
  error: string | null;
  setError: (value: string | null) => void;
};

export const useCapturedAudioPlayback = ({
  audioRecordingId,
  canPlay,
}: {
  audioRecordingId: Uuid | null;
  canPlay: boolean;
}): CapturedAudioPlayback => {
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const playbackUrlRef = useRef<string | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revokePlaybackUrl = useCallback(() => {
    const element = audioElementRef.current;
    if (element) {
      element.pause();
      element.removeAttribute("src");
      element.load();
    }

    const current = playbackUrlRef.current;
    if (current) {
      if (current.startsWith("blob:")) {
        URL.revokeObjectURL(current);
      }
      playbackUrlRef.current = null;
    }

    setPlaybackUrl(null);
  }, []);

  const clear = useCallback(() => {
    revokePlaybackUrl();
    setError(null);
  }, [revokePlaybackUrl]);

  const play = useCallback(async () => {
    if (!canPlay) {
      setError("Playback is unavailable until processing finishes.");
      return;
    }
    if (!audioRecordingId) {
      setError("Audio recording is unavailable.");
      return;
    }

    revokePlaybackUrl();
    setIsPreparing(true);
    setError(null);

    try {
      const url = await prepareAudioRecordingDownloadUrl({
        audioRecordingId: String(audioRecordingId),
      });
      playbackUrlRef.current = url;
      setPlaybackUrl(url);
      const element = audioElementRef.current;
      if (element) {
        element.src = url;
        await element.play();
      }
    } catch (unknownErr) {
      const message =
        unknownErr instanceof Error ? unknownErr.message : "Failed to play recorded audio.";
      setError(message);
      revokePlaybackUrl();
    } finally {
      setIsPreparing(false);
    }
  }, [audioRecordingId, canPlay, revokePlaybackUrl]);

  return {
    audioElementRef,
    playbackUrl,
    play,
    clear,
    isPreparing,
    error,
    setError,
  };
};
